import { useState, useEffect, useRef, useMemo } from 'react'

import { useDebounceCallback } from '@buffer-mono/popcorn'

import { isSafari } from '~publish/legacy/composer/composer/utils/DOMUtils'
import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'

/**
 * Hook for checking the selection in the editor.
 *
 * @param editor - The editor to check the selection in.
 * @param draftId - The id of the draft to check the selection in.
 * @returns An object with the selected text and a boolean indicating if there is a selection.
 */
export const useEditorSelection = (
  editor: BufferEditor | null | undefined,
  draftId: string,
): {
  selectedText: string | null
  hasSelection: boolean
  latestSelection: string | null
} => {
  const [selectedText, setSelectedText] = useState<string | null>(null)
  const latestSelectionRef = useRef<string | null>(null)

  // Memoize the selector to avoid recreating it on every render
  const editorSelector = useMemo(() => `[data-draftid="${draftId}"]`, [draftId])

  const debouncedSetSelectedText = useDebounceCallback(
    (text: string | null) => {
      setSelectedText(text)
    },
    100,
  )

  useEffect(() => {
    if (!editor) {
      return
    }

    const checkSelection = (): void => {
      const container = document.querySelector(editorSelector)
      const hasEditorFocus = document.activeElement === container

      if (hasEditorFocus && editor.getSelection) {
        const selection = editor.getSelection()
        latestSelectionRef.current = selection
        debouncedSetSelectedText(selection)
      } else {
        latestSelectionRef.current = null
        // Clear selection immediately when focus is lost
        setSelectedText(null)
      }
    }

    // Check on mount immediately
    checkSelection()

    const handleSelectionChange = (): void => {
      checkSelection()
    }

    // Safari-specific click handler to detect when editor loses focus
    const handleDocumentClick = (event: MouseEvent): void => {
      const container = document.querySelector(editorSelector)
      const target = event.target
      const clickOutside =
        target instanceof Node && !container?.contains(target)

      if (clickOutside && document.activeElement !== container) {
        setSelectedText(null)
      }
    }

    // Use the editor's selection check if available
    editor.onSelectionCheck?.(handleSelectionChange)

    // Safari handles selection events differently
    if (!isSafari()) {
      document.addEventListener('selectionchange', handleSelectionChange)
    } else {
      // Safari workaround: use click event to detect focus loss
      document.addEventListener('click', handleDocumentClick)
    }

    document.addEventListener('mouseup', handleSelectionChange)

    return (): void => {
      if (!isSafari()) {
        document.removeEventListener('selectionchange', handleSelectionChange)
      } else {
        document.removeEventListener('click', handleDocumentClick)
      }
      document.removeEventListener('mouseup', handleSelectionChange)
      setSelectedText(null)
    }
  }, [editor, draftId, debouncedSetSelectedText, editorSelector])

  return {
    selectedText,
    hasSelection: !!selectedText,
    latestSelection: latestSelectionRef.current,
  }
}
